import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";

import { Alert } from "@mui/material";

import { useEditingContext } from "@features/editor/Context";
import { useDistributorOrderingContext } from "@features/orderingCtx/DistributorOrderingContext";
import { Button } from "@features/ui";
import { Address, AddressType } from "@models/Address";
import { useHasPermission } from "@utils/permissions";

import {
  AddressFormValues,
  formValuesToMutationAttributes,
  getAddressFormDefaultValues,
  pickDirtyMutationFields,
} from "../addressFormHelpers";
import {
  useCreateAddressMutation,
  useUpdateAddressMutation,
} from "../addressQueries";
import { AddressFields } from "./AddressFields";

export const BranchCustomAddressForm = ({
  onSuccess,
}: {
  onSuccess: (address: Address) => void;
}) => {
  const {
    orderingCtx: { marketId, territoryId },
  } = useDistributorOrderingContext();
  const { subject: address, isEditing } = useEditingContext<Address>();
  const hasPermission = useHasPermission();

  const canCreate = hasPermission("address:create");
  const canUpdate = hasPermission("address:update");
  const canEditCoreFields =
    ((!isEditing && canCreate) || (isEditing && canUpdate)) &&
    address?.type !== AddressType.Branch;
  const canEditPhone = (!isEditing && canCreate) || (isEditing && canUpdate);
  const canMakeChanges = canEditCoreFields || canEditPhone;

  const formMethods = useForm<AddressFormValues>({
    defaultValues: getAddressFormDefaultValues(address),
    mode: "onChange",
  });

  const {
    handleSubmit,
    getValues,
    formState: { isDirty, dirtyFields },
  } = formMethods;

  const createMutation = useCreateAddressMutation();
  const updateMutation = useUpdateAddressMutation();

  const isPending = createMutation.isPending || updateMutation.isPending;
  const errorMsg =
    createMutation.error?.message || updateMutation.error?.message;

  const submitForm = () => {
    const formValues = getValues();
    const dirtyKeys = Object.keys(dirtyFields);

    if (!isEditing) {
      const createPromise = createMutation.mutateAsync({
        ...formValuesToMutationAttributes(formValues),
        country: "USA",
        ...(marketId
          ? { marketId, type: AddressType.BranchCustom }
          : { type: AddressType.Custom, territoryId }),
      });
      toast
        .promise(createPromise, {
          loading: `Saving address for ${formValues.name}`,
          success: (data) => `Address for ${data.name} saved`,
          error: () => `Error saving address for ${formValues.name}`,
        })
        .then((data) => onSuccess(data));
      return;
    }

    const updatePromise = updateMutation.mutateAsync({
      ...pickDirtyMutationFields(formValues, dirtyKeys),
      id: address.id,
    });
    toast
      .promise(updatePromise, {
        loading: `Updating address for ${formValues.name}`,
        success: (data) => `Address for ${data.name} updated`,
        error: () => `Error updating address for ${formValues.name}`,
      })
      .then((data) => onSuccess(data));
  };

  return (
    <FormProvider {...formMethods}>
      <div className="flex flex-col gap-6">
        <AddressFields
          nameLabel="Business Name"
          phoneRequired
          disabled={!canEditCoreFields}
          disablePhone={!canEditPhone}
          usesTerritorySubState={Boolean(territoryId)}
          isEditing={isEditing}
          showReyesFields
        />

        {canMakeChanges && (
          <div className="flex place-content-end gap-3 flex-wrap">
            <Button
              variant="contained"
              disabled={!isDirty && isEditing}
              loading={isPending}
              onClick={handleSubmit(submitForm)}
            >
              {isEditing ? "Update" : "Create"}
            </Button>
          </div>
        )}
        {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
      </div>
    </FormProvider>
  );
};
